import React from 'react'
import { Head } from '@inertiajs/react'
import {
  FileText, Tag, Eye, Edit3, ArrowRight, Image, Users,
  Clock,
} from 'lucide-react'
import { AdminLayout } from '@/components/admin/templates/AdminLayout'
import { Surface } from '@/components/admin/atoms/layout/Surface'
import { Stack } from '@/components/admin/atoms/layout/Stack'
import { Inline } from '@/components/admin/atoms/layout/Inline'
import { Grid } from '@/components/admin/atoms/layout/Grid'
import { Padding } from '@/components/admin/atoms/layout/Padding'
import { ScrollArea } from '@/components/admin/atoms/layout/ScrollArea'
import { Heading } from '@/components/admin/atoms/typography/Heading'
import { Text } from '@/components/admin/atoms/typography/Text'
import { Badge } from '@/components/admin/atoms/feedback/Badge'
import { EmptyState } from '@/components/admin/atoms/feedback/EmptyState'
import { LinkButton } from '@/components/admin/atoms/actions/LinkButton'
import { StatCard } from '@/components/admin/molecules/data/StatCard'
import { DataTable } from '@/components/admin/atoms/data/DataTable'
import { DataTableHead } from '@/components/admin/atoms/data/DataTableHead'
import { DataTableHeaderCell } from '@/components/admin/atoms/data/DataTableHeaderCell'
import { DataTableBody } from '@/components/admin/atoms/data/DataTableBody'
import { DataTableRow } from '@/components/admin/atoms/data/DataTableRow'
import { DataTableCell } from '@/components/admin/atoms/data/DataTableCell'
import { PageHeader } from '@/components/admin/organisms/PageHeader'
import { formatDate } from '@/lib/utils'
import type { PageProps } from '@/types/inertia.d'
import type { DashboardStats, RecentPost } from '@/types/admin'

type Props = PageProps<{ stats: DashboardStats }>

const statusVariantMap: Record<string, 'success' | 'warning' | 'ghost'> = {
  published: 'success',
  draft: 'warning',
}

const Dashboard: React.FC<Props> = ({ stats }) => (
  <AdminLayout>
    <Head title="Dashboard" />
    <Stack gap="6">
      <PageHeader
        title="Dashboard"
        description="Welcome back! Here's what's happening."
        action={{
          label: 'New Post',
          href: '/admin/posts/new',
          icon: <Edit3 style={{ width: 16, height: 16 }} />,
        }}
      />

      <Grid cols="4" gap="4">
        <StatCard
          label="Total Posts"
          value={stats.totalPosts}
          icon={<FileText style={{ width: 24, height: 24, color: "#fff" }} />}
          color="primary"
        />
        <StatCard
          label="Published"
          value={stats.publishedPosts}
          icon={<Eye style={{ width: 24, height: 24, color: "#fff" }} />}
          color="success"
        />
        <StatCard
          label="Drafts"
          value={stats.draftPosts}
          icon={<Edit3 style={{ width: 24, height: 24, color: "#fff" }} />}
          color="warning"
        />
        <StatCard
          label="Scheduled"
          value={stats.scheduledPosts}
          icon={<Clock style={{ width: 24, height: 24, color: "#fff" }} />}
          color="info"
        />
      </Grid>

      <Grid cols="3" gap="4">
        <StatCard
          label="Total Tags"
          value={stats.totalTags}
          icon={<Tag style={{ width: 20, height: 20 }} />}
          color="muted"
        />
        <StatCard
          label="Total Users"
          value={stats.totalUsers}
          icon={<Users style={{ width: 20, height: 20 }} />}
          color="muted"
        />
        <StatCard
          label="Media Files"
          value={stats.totalMedia}
          icon={<Image style={{ width: 20, height: 20 }} />}
          color="muted"
        />
      </Grid>

      <Surface variant="card" radius="lg">
        <Padding all="6">
          <Stack gap="4">
            <Inline justify="between" align="center">
              <Heading level={4}>Recent Posts</Heading>
              <LinkButton href="/admin/posts" variant="link">
                View all <ArrowRight style={{ width: 14, height: 14 }} />
              </LinkButton>
            </Inline>

            {stats.recentPosts.length === 0 ? (
              <EmptyState
                title="No posts yet"
                description="Create your first post to get started."
                action={
                  <LinkButton href="/admin/posts/new" variant="link">
                    Create your first post
                  </LinkButton>
                }
              />
            ) : (
              <ScrollArea horizontal>
                <DataTable>
                  <DataTableHead>
                    <DataTableHeaderCell>Title</DataTableHeaderCell>
                    <DataTableHeaderCell>Author</DataTableHeaderCell>
                    <DataTableHeaderCell>Status</DataTableHeaderCell>
                    <DataTableHeaderCell>Date</DataTableHeaderCell>
                    <DataTableHeaderCell width="64px" />
                  </DataTableHead>
                  <DataTableBody>
                    {stats.recentPosts.map((post: RecentPost) => (
                      <DataTableRow key={post.id}>
                        <DataTableCell>
                          <Text size="sm" weight="medium">{post.title}</Text>
                        </DataTableCell>
                        <DataTableCell>
                          <Text size="sm" color="muted">{post.author ?? '---'}</Text>
                        </DataTableCell>
                        <DataTableCell>
                          <Badge variant={statusVariantMap[post.status] ?? 'ghost'}>
                            {post.status}
                          </Badge>
                        </DataTableCell>
                        <DataTableCell>
                          <Text size="sm" color="muted">{formatDate(post.created_at)}</Text>
                        </DataTableCell>
                        <DataTableCell>
                          <LinkButton
                            href={`/admin/posts/${post.id}/edit`}
                            variant="ghost"
                            size="xs"
                          >
                            Edit
                          </LinkButton>
                        </DataTableCell>
                      </DataTableRow>
                    ))}
                  </DataTableBody>
                </DataTable>
              </ScrollArea>
            )}
          </Stack>
        </Padding>
      </Surface>
    </Stack>
  </AdminLayout>
)

export default Dashboard
